import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  toPascalCase,
  SignOutHandler,
  UserDetails,
  NavigateConst,
  getDecryptedValue,
  isGuest,
  ANALYTICS_MESSAGES,
  useAnalytics
} from 'common';
import { GlobalContext } from 'context';
import { Button, UserIconBlack, DropdownToggleIcon, ModalMultiUse } from '@sentara-health/optimahealth_web';

// Define the Logout component
const Logout = () => {
  // Initialize translation function
  const { t } = useTranslation();
  const [guestValue, setGuestValue] = useState<boolean>(false);
  const [loggedOutModal, setLoggedOutModal] = useState<boolean>(false);

  // Fetch userLogout function from API hook
  const signOut = SignOutHandler();
  const { getFeature } = useContext(GlobalContext);

  // Initialize navigation function
  const navigate = useNavigate();

  // State variables to manage user's name
  const [name, setName] = useState('');

  // useEffect hook to update the name state with the user's first name from localStorage
  useEffect(() => {
    // Fetch the user's first name from localStorage or set an empty string if not found
    (async () => {
      const personalInfo = await getDecryptedValue(UserDetails.guest);
      const firstName = JSON.parse(personalInfo ?? '{}');
      setName(firstName?.user?.firstName ?? '');
      const guestValue: boolean = await isGuest();
      setGuestValue(guestValue);
    })();
  }, [name]);

  // Convert the user's name to PascalCase
  const capitalizedText = toPascalCase(name);
  const navigateToProfile = () => {
    navigate(NavigateConst.profile)
  };
  const { trackContentListing } = useAnalytics();

  return (
    <div className="d-xl-block d-lg-block d-md-block position-relative">
      <div className="d-flex align-items-center">
        <div>
          {/* Dropdown button to show user profile information */}
          <Button
            iconLeft={<UserIconBlack />}
            iconRight={<DropdownToggleIcon />}
            dataTestId="usernameButton"
            id="btnGroupDrop1"
            type="button"
            variant="dropdownmenu_btn"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {/* Render the user's name with a title attribute showing the capitalized version */}
            <span
              className="text-truncate"
              title={capitalizedText}
            >
              {t('common.hello')}, {capitalizedText}
            </span>
          </Button>


          {/* Dropdown menu to show user options */}
          <ul
            className="dropdown-menu dropdown-menu-custom"
            aria-labelledby="btnGroupDrop1"
          >
            {getFeature?.MyProfile && !guestValue && (
              <li>
                {/* Button to navigate to the user's profile page */}
                <Button
                  dataTestId={'profileButton'}
                  variant='dropdown-item'
                  onClick={navigateToProfile}
                >
                  {t('common.profile')}
                </Button>
              </li>
            )}
            <li>
              {/* Button to trigger the signOutHandler function */}
              <Button
                dataTestId={'submitButton'}
                variant='dropdown-item'
                onClick={() => setLoggedOutModal(true)}
              >
                {t('common.logout')}
              </Button>
            </li>
          </ul>
        </div>
      </div>
      {/* Modal to confirm logout action */}
      <ModalMultiUse
        isOpen={loggedOutModal}
        onClose={() => setLoggedOutModal(false)}
        body={t('common.logOutModalDescription')}
        bodyClass="text-center"
        footer={
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId="confirmLogoutButton"
                variant="primary_btn"
                className="btn-md-height"
                onClick={() => {
                  signOut();
                  setLoggedOutModal(false);
                  trackContentListing(
                    '403',
                    ANALYTICS_MESSAGES.POP_USER_SIGN_OUT,
                    1
                  );
                }}
              >
                {t('common.logout')}
              </Button>
            </div>
            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId="cancelLogoutButton"
                variant="secondary_btn"
                className="btn-md-height"
                onClick={() => {
                  setLoggedOutModal(false);
                  trackContentListing(
                    '404',
                    ANALYTICS_MESSAGES.POP_USER_SIGN_OUT_CANCEL,
                    1
                  );
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </>
        }
        variant="defaultNoPbBody"
        data-testid="logoutConfirmationModal"
      />
    </div>
  );
};

// Export the Logout component as the default export
export default Logout;